const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:3001";

export const IDENTITYIQ_LINK =
  "https://member.identityiq.com/sc-securemax.aspx?offercode=431275W6&utm_medium=social&utm_source=linktree&utm_campaign=get+all+3+credit+reports+for+$1+equifax/experian/transunion&utm_content=link_in_bio";

function getStoredToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    ""
  );
}

async function request(path, options = {}) {
  const isFormData = options.body instanceof FormData;
  const token = getStoredToken();

  let body = options.body;

  if (
    body &&
    !isFormData &&
    typeof body === "object" &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer)
  ) {
    body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    body,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  /*
   * Handle expired or invalid login sessions globally.
   * Do not redirect during an unsuccessful login or registration attempt.
   */
  const isAuthRequest =
    path.startsWith("/auth/login") ||
    path.startsWith("/auth/register");

  if (response.status === 401 && !isAuthRequest) {
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");

    if (window.location.pathname !== "/login") {
      window.location.replace("/login?reason=session-expired");
    }

    throw new Error("Your session expired. Please sign in again.");
  }

  if (!response.ok) {
    const text = await response.text();

    let message = text || `Request failed: ${response.status}`;

    try {
      const parsed = JSON.parse(text);
      message =
        parsed?.detail ||
        parsed?.message ||
        message;
    } catch {
      // The response was plain text, so keep the original message.
    }

    throw new Error(message);
  }

  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return response.json();
  }

  return response.text();
}

/* -----------------------------
   Auth helpers
----------------------------- */
export function getToken() {
  return getStoredToken();
}

export function setToken(token) {
  localStorage.setItem("token", token);
  localStorage.setItem("access_token", token);
}

export function clearToken() {
  localStorage.removeItem("token");
  localStorage.removeItem("access_token");
  localStorage.removeItem("user");
}

export function getCurrentUser() {
  const storedUser = localStorage.getItem("user");

  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser);
  } catch {
    localStorage.removeItem("user");
    return null;
  }
}

export function getCurrentRole() {
  return String(getCurrentUser()?.role || "").toLowerCase();
}

export function hasRole(...allowedRoles) {
  const currentRole = getCurrentRole();

  return allowedRoles
    .map((role) => String(role).toLowerCase())
    .includes(currentRole);
}

export async function login(email, password) {
  const data = await request("/auth/login", {
    method: "POST",
    body: { email, password },
  });

  if (data?.access_token) {
    setToken(data.access_token);
  }

  if (data?.user) {
    localStorage.setItem("user", JSON.stringify(data.user));
  }

  if (data?.company) {
    localStorage.setItem("company", JSON.stringify(data.company));
  } else {
    localStorage.removeItem("company");
  }

  return data;
}

export async function register(
  fullName,
  companyName,
  email,
  password
) {
  return request("/auth/register", {
    method: "POST",
    body: {
      full_name: fullName,
      company_name: companyName,
      email,
      password,
    },
  });
}

export const loginUser = login;
export const registerUser = register;

/* -----------------------------
   Employees / Team
----------------------------- */
export const getEmployees = () => request("/employees");

export const createEmployee = (data) =>
  request("/employees", {
    method: "POST",
    body: data,
  });

export const updateEmployee = (employeeId, data) =>
  request(`/employees/${employeeId}`, {
    method: "PUT",
    body: data,
  });

/* -----------------------------
   Clients
----------------------------- */
export const getClients = () => request("/clients");
export const getClient = (id) => request(`/clients/${id}`);

export const createClient = (data) =>
  request("/clients", {
    method: "POST",
    body: data,
  });

export const updateClient = (id, data) =>
  request(`/clients/${id}`, {
    method: "PUT",
    body: data,
  });

export const deleteClient = (id) =>
  request(`/clients/${id}`, {
    method: "DELETE",
  });

/* -----------------------------
   Letters
----------------------------- */
export const getLetters = () => request("/letters");

export const getLetter = (id) =>
  request(`/letters/${id}`);

export const createLetter = (data) =>
  request("/letters", {
    method: "POST",
    body: data,
  });

export const deleteLetter = (id) =>
  request(`/letters/${id}`, {
    method: "DELETE",
  });

export const generateAllBureauLetters = (data) =>
  request("/generate-all-bureau-letters", {
    method: "POST",
    body: data,
  });

export async function getLettersForClient(clientId) {
  const letters = await getLetters();

  return Array.isArray(letters)
    ? letters.filter(
        (item) =>
          String(item.client_id) === String(clientId)
      )
    : [];
}

/* -----------------------------
   My Work Queue
----------------------------- */
export const getMyWorkQueue = () =>
  request("/my-work-queue");

export const updateTask = (taskId, data) =>
  request(`/tasks/${taskId}`, {
    method: "PUT",
    body: data,
  });

export async function generateAllAccountDisputes(
  clientId,
  accounts = [],
  bureau = "Experian",
  onProgress = null
) {
  if (!clientId) {
    throw new Error(
      "A client must be selected before generating letters."
    );
  }

  const disputeAccounts = accounts.filter((account) => {
    return (
      account.dispute_candidate === true ||
      account.dispute_candidate === 1 ||
      account.dispute_candidate === "true"
    );
  });

  if (disputeAccounts.length === 0) {
    throw new Error(
      "No dispute-candidate accounts were found."
    );
  }

  const letters = [];

  for (
    let index = 0;
    index < disputeAccounts.length;
    index++
  ) {
    const account = disputeAccounts[index];

    const formData = new FormData();

    const accountNumber =
      account.account_number ||
      account.accountNumber ||
      "N/A";

    const recommendedLetterType =
      account.recommended_letter ||
      account.recommendedLetter ||
      account.letter_type ||
      "General Dispute";

    const accountBureau =
      account.bureau ||
      bureau ||
      "Experian";

    const creditorName =
      account.creditor ||
      "Unknown Creditor";

    const disputeReason =
      account.suggested_action ||
      account.suggestedAction ||
      "Please investigate this account for accuracy, completeness, and compliance.";

    formData.append(
      "client_id",
      String(clientId)
    );

    formData.append("bureau", accountBureau);
    formData.append("creditor", accountBureau);
    formData.append(
      "creditor_name",
      creditorName
    );

    formData.append(
      "template",
      recommendedLetterType
    );

    formData.append(
      "letter_type",
      recommendedLetterType
    );

    formData.append(
      "account",
      accountNumber
    );

    formData.append(
      "account_number",
      accountNumber
    );

    formData.append(
      "details",
      "Creditor / Furnisher: " +
        creditorName +
        "\nAccount Number: " +
        accountNumber +
        "\nReason: " +
        disputeReason
    );

    const letter =
      await generateLetter(formData);

    if (!letter?.id) {
      throw new Error(
        `A valid letter was not created for ${creditorName}.`
      );
    }

    if (account?.id) {
      await updateCreditReportAccountLetter(
        account.id,
        letter.id
      );
    }

    letters.push(letter);

    if (
      typeof onProgress === "function"
    ) {
      onProgress(
        index + 1,
        disputeAccounts.length
      );
    }
  }

  return letters;
}

/* -----------------------------
   Disputes
----------------------------- */
export const getDisputes = () =>
  request("/disputes");

export const getDispute = (id) =>
  request(`/disputes/${id}`);

export const createDispute = (data) =>
  request("/disputes", {
    method: "POST",
    body: data,
  });

export const deleteDispute = (id) =>
  request(`/disputes/${id}`, {
    method: "DELETE",
  });

export async function getDisputesForClient(clientId) {
  const disputes = await getDisputes();

  return Array.isArray(disputes)
    ? disputes.filter(
        (item) =>
          String(item.client_id) ===
          String(clientId)
      )
    : [];
}

/* -----------------------------
   Dashboard / stats
----------------------------- */
export const getStats = () =>
  request("/stats");

export const getDashboardStats = () =>
  request("/dashboard-stats");

export const getActivity = () =>
  request("/activity");

export const getActivityLog = () =>
  request("/activity");

export const getAnalytics = () =>
  request("/analytics");

export const getAnalyticsOverview = () =>
  request("/analytics");

export async function getDashboardActivity() {
  return request("/dashboard-activity");
}

/* -----------------------------
   Documents
----------------------------- */
export const getDocuments = () =>
  request("/documents");

export async function uploadDocument(formData) {
  return request("/documents", {
    method: "POST",
    body: formData,
  });
}

export async function deleteDocument(id) {
  return request(`/documents/${id}`, {
    method: "DELETE",
  });
}

export function getDocumentUrl(id = "") {
  return `${API_BASE}/documents/${id}`;
}

/* -----------------------------
   Credit reports
----------------------------- */
export const getCreditReports = () =>
  request("/credit-reports");

export const uploadCreditReport = (formData) =>
  request("/upload-credit-report", {
    method: "POST",
    body: formData,
  });

export const deleteCreditReport = (id) =>
  request(`/credit-reports/${id}`, {
    method: "DELETE",
  });

export async function importCreditReport(_formData) {
  return { success: true };
}

export async function getCreditReportAudit(_id) {
  return [];
}

export async function clearCreditReportAccounts(
  clientId
) {
  return request(
    `/credit-report-accounts/${clientId}`,
    {
      method: "DELETE",
    }
  );
}

/* -----------------------------
   Credit cases
----------------------------- */
export const getCreditCases = () =>
  request("/credit-cases");

export const getCreditCase = (caseId) =>
  request(`/credit-cases/${caseId}`);

export const createCreditCase = (data) =>
  request("/credit-cases", {
    method: "POST",
    body: data,
  });

/* -----------------------------
   Notes / tasks
----------------------------- */
export const getNotesTasks = () =>
  request("/tasks");

export const createNoteTask = (data) =>
  request("/tasks", {
    method: "POST",
    body: data,
  });

export const updateNoteTask = (id, data) =>
  request(`/tasks/${id}`, {
    method: "PUT",
    body: data,
  });

export const deleteNoteTask = (id) =>
  request(`/tasks/${id}`, {
    method: "DELETE",
  });

/* -----------------------------
   Letter generation
----------------------------- */
export async function generateLetter(data) {
  return request("/generate-letter", {
    method: "POST",
    body: data,
  });
}

export async function createLetterPreview(data) {
  return request("/create-letter", {
    method: "POST",
    body: data,
  });
}

export async function saveCreditReportAccounts(data) {
  return request(
    "/credit-report-accounts",
    {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
}

export async function updateCreditReportAccountLetter(
  accountId,
  generatedLetterId
) {
  return request(
    `/credit-report-accounts/${accountId}/generated-letter`,
    {
      method: "PATCH",
      body: JSON.stringify({
        generated_letter_id:
          generatedLetterId,
      }),
      headers: {
        "Content-Type":
          "application/json",
      },
    }
  );
}

export async function getCreditReportAccounts(
  clientId = ""
) {
  const query = clientId
    ? `?client_id=${clientId}`
    : "";

  return request(
    `/credit-report-accounts${query}`
  );
}

export async function deleteCreditReportAccounts(
  clientId = ""
) {
  const query = clientId
    ? `?client_id=${clientId}`
    : "";

  return request(
    `/credit-report-accounts${query}`,
    {
      method: "DELETE",
    }
  );
}

export function getLetterPdfUrl(id) {
  return `${API_BASE}/create-letter-pdf/${id}`;
}

export const updateLetterStatus = (
  id,
  status
) =>
  request(
    `/letters/${id}/status?status=${encodeURIComponent(
      status
    )}`,
    {
      method: "PUT",
    }
  );

export async function createCheckoutSession(plan) {
  return request("/stripe/create-checkout-session", {
    method: "POST",
    body: { plan },
  });
}

export async function getSubscriptionStatus() {
  return request("/stripe/subscription-status");
}

export async function cancelSubscription() {
  return request("/stripe/cancel-subscription", { method: "POST" });
}
